/**
 * ASCII-sanitiser for jsPDF text (pure, exported for unit tests).
 *
 * The SOP download (SopsPage -> downloadSopFile) builds the PDF with jsPDF's
 * STANDARD fonts (helvetica). Those fonts are limited to WinAnsi/Latin-1 and,
 * in practice, jsPDF renders any character outside printable ASCII/Latin-1 as
 * garbage -- e.g. the flow-diagram text "Start -> Review" and "CR Approved?
 * -[NO]-> ..." came out as "Start !' Review" / "--[NO]!'", and bullets as a
 * replacement glyph.
 *
 * The real flowchart markup (SopFlowDiagram.reactflowToText) deliberately uses
 * U+2192 arrows and em-dashes, and the renderer elsewhere emits bullets,
 * smart quotes and ellipses. Rather than embed a full Unicode TTF (hundreds of
 * KB into the bundle), map the symbols people actually use to safe ASCII and
 * drop anything else that the standard font cannot draw. Applied to EVERY
 * string handed to doc.text / doc.splitTextToSize, so the whole PDF -- not just
 * the flowchart -- stays legible.
 *
 * Implemented as a numeric code-point lookup (not \uXXXX regex escapes) so the
 * source contains no invisible characters and iterates by code point, which
 * means an astral char (e.g. an emoji) collapses to a single '?'.
 */

// code point -> ASCII replacement. Built once at module load.
const MAP = Object.create(null)
const put = (value, ...codePoints) => {
  for (const cp of codePoints) MAP[cp] = value
}

// Rightward arrows (flow order)
put('->', 0x2192, 0x2794, 0x2799, 0x279c, 0x279d, 0x279e, 0x279f, 0x27a1, 0x27a4, 0x21a6, 0x21e8, 0x21fe, 0x27f6, 0x2b62, 0x2b95)
put('=>', 0x21d2, 0x27f9, 0x2b46)
// Leftward arrows
put('<-', 0x2190, 0x21e6, 0x21fd, 0x27f5, 0x2b60)
put('<=', 0x21d0, 0x27f8)
// Bidirectional arrows
put('<->', 0x2194, 0x27f7)
put('<=>', 0x21d4, 0x27fa)
// Up / down arrows
put('^', 0x2191, 0x21e7, 0x2b06)
put('v', 0x2193, 0x21e9, 0x2b07, 0x21b3, 0x2937)
// Dashes (en/em/figure/minus)
put('-', 0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2015, 0x2212)
// Bullets / list markers
put('-', 0x00b7, 0x2022, 0x2023, 0x2043, 0x2219, 0x25aa, 0x25ab, 0x25cf, 0x25cb, 0x25e6, 0x25b6, 0x25b8, 0x2bc8, 0x2756)
// Single quotes / primes
put("'", 0x2018, 0x2019, 0x201a, 0x201b, 0x2032, 0x00b4, 0x2035)
// Double quotes / primes / guillemets
put('"', 0x201c, 0x201d, 0x201e, 0x201f, 0x2033, 0x2036, 0x00ab, 0x00bb)
// Ellipsis
put('...', 0x2026)
// Check / cross marks
put('[x]', 0x2713, 0x2714, 0x2705)
put('[ ]', 0x2717, 0x2718, 0x2715, 0x274c, 0x2716)
// Non-breaking / thin spaces -> normal space
put(' ', 0x00a0, 0x2007, 0x2008, 0x2009, 0x200a, 0x202f)
// Zero-width / BOM -> drop
put('', 0x200b, 0x200c, 0x200d, 0xfeff)

/** Map common Unicode punctuation/symbols to ASCII and strip anything the
 *  jsPDF standard fonts cannot render. Returns a plain-ASCII/Latin-1 string. */
export function pdfSafeText(text) {
  if (text === null || text === undefined) return ''
  let out = ''
  // for..of iterates by code point, so surrogate pairs stay intact.
  for (const ch of String(text)) {
    const cp = ch.codePointAt(0)
    const mapped = MAP[cp]
    if (mapped !== undefined) {
      out += mapped
    } else if (cp === 0x09 || cp === 0x0a || cp === 0x0d) {
      out += ch // tab / newline / carriage return
    } else if (cp >= 0x20 && cp <= 0x7e) {
      out += ch // printable ASCII
    } else if (cp >= 0xa0 && cp <= 0xff) {
      out += ch // Latin-1 supplement (accented letters)
    } else {
      out += '?' // anything the standard font cannot draw
    }
  }
  return out
}
